import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsButtonV2Component } from '../buttons/ds-button-v2.component';

export type DsInputOtpState = 'default' | 'error' | 'disabled';
export type DsInputOtpSendMethod = 'correo' | 'telefono';

@Component({
  selector: 'ds-input-otp',
  standalone: true,
  imports: [CommonModule, DsButtonV2Component],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-otp"
      [class.ds-otp--error]="state === 'error'"
      [class.ds-otp--disabled]="isDisabled"
      [class.ds-otp--length-4]="normalizedLength === 4"
      [attr.aria-disabled]="isDisabled || null"
    >
      <div class="ds-otp__content">
        <div class="ds-otp__text">
          <div class="ds-otp__label">{{ resolvedLabel }}</div>
          <div class="ds-otp__message">{{ resolvedMessage }}</div>
        </div>

        <div class="ds-otp__cells">
          <span
            *ngFor="let index of cellIndexes"
            class="ds-otp__cell"
            [class.ds-otp__cell--filled]="!!digitAt(index)"
            [class.ds-otp__cell--active]="isFocused && index === activeIndex"
          >
            <input
              #cellControl
              class="ds-otp__cell-input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              autocomplete="one-time-code"
              [value]="digitAt(index)"
              [disabled]="isDisabled"
              [attr.aria-label]="resolvedLabel + ' digito ' + (index + 1)"
              [attr.aria-invalid]="state === 'error' ? 'true' : null"
              [attr.aria-describedby]="state === 'error' ? helpId : null"
              (focus)="onCellFocus(index, $event)"
              (blur)="onCellBlur($event)"
              (beforeinput)="onCellBeforeInput(index, $event)"
              (input)="onCellInput(index, $event)"
              (keydown)="onCellKeydown(index, $event)"
              (paste)="onCellPaste($event)"
            />
            <span *ngIf="digitAt(index) && isDigitRevealed(index)" class="ds-otp__digit" aria-hidden="true">
              {{ digitAt(index) }}
            </span>
            <span *ngIf="digitAt(index) && !isDigitRevealed(index)" class="ds-otp__dot" aria-hidden="true"></span>
          </span>
        </div>

        <div *ngIf="state === 'error'" class="ds-otp__help" [attr.id]="helpId" role="alert">
          {{ helpText }}
        </div>

        <div *ngIf="showResendCode" class="ds-otp__resend">
          <span *ngIf="isResendCounting" class="ds-otp__resend-label">{{ resendPrompt }}</span>
          <span *ngIf="isResendCounting" class="ds-otp__resend-waiting">
            {{ resendWaitingText }} <strong>{{ formattedResendTime }}</strong>
          </span>
          <ds-button-v2
            *ngIf="!isResendCounting"
            [label]="resendButtonLabel"
            emphasis="secondary"
            size="xs"
            (clicked)="handleResend($event)"
          />
        </div>
      </div>

      <div *ngIf="showContinueButton" class="ds-otp__continue">
        <ds-button-v2
          [label]="continueLabel"
          emphasis="primary"
          size="m"
          [disabled]="isContinueDisabled"
          [fullWidth]="true"
          (clicked)="continueClicked.emit($event)"
        />
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      max-width: calc((var(--ds-input-field-height) * 6) + (var(--ds-input-gap) * 5));
    }

    .ds-otp {
      --ds-otp-cell-width: min(
        var(--ds-input-field-height),
        calc((100% - (var(--ds-input-gap) * 5)) / 6)
      );

      display: flex;
      flex-direction: column;
      gap: calc(var(--ds-input-gap) * 2);
      width: 100%;
      min-width: 0;
      font-family: 'Sura Sans', system-ui, sans-serif;
    }

    .ds-otp--length-4 {
      --ds-otp-cell-width: min(
        var(--ds-input-field-height),
        calc((100% - (var(--ds-input-gap) * 3)) / 4)
      );
    }

    .ds-otp__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      width: 100%;
      min-width: 0;
    }

    .ds-otp__text {
      display: flex;
      flex-direction: column;
      gap: var(--ds-input-gap);
      width: 100%;
    }

    .ds-otp__label {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 700;
      font-size: var(--ds-foundation-font-size-s);
      line-height: var(--ds-foundation-line-height-s);
      color: var(--ds-input-label-color);
      text-align: center;
      letter-spacing: 0;
    }

    .ds-otp__message {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      font-size: var(--ds-foundation-font-size-xs);
      line-height: var(--ds-foundation-line-height-xs);
      color: var(--ds-input-value-color);
      text-align: center;
      letter-spacing: 0;
    }

    .ds-otp__cells {
      display: flex;
      justify-content: center;
      gap: var(--ds-input-gap);
      width: 100%;
      min-width: 0;
      box-sizing: border-box;
      padding-block: var(--ds-foundation-spacing-m);
    }

    .ds-otp__cell {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-otp-cell-width);
      height: var(--ds-input-field-height);
      box-sizing: border-box;
      flex: 0 1 var(--ds-otp-cell-width);
      padding: 0;
      background: var(--ds-input-background);
      border: var(--ds-input-border-width) solid var(--ds-input-border-color);
      border-radius: var(--ds-input-border-radius);
      color: transparent;
      caret-color: transparent;
      font-size: 0;
      line-height: 1;
      text-align: center;
    }

    .ds-otp--error .ds-otp__cell {
      border-color: var(--ds-input-border-color-error);
    }

    .ds-otp__cell--active {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-otp__cell-input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 0;
      border: 0;
      background: transparent;
      color: transparent;
      caret-color: transparent;
      font-size: 16px;
      text-align: center;
      appearance: none;
      outline: none;
    }

    .ds-otp__dot {
      width: calc(var(--ds-input-field-height) / 5);
      height: calc(var(--ds-input-field-height) / 5);
      border-radius: 999px;
      background: var(--ds-input-value-color);
      pointer-events: none;
    }

    .ds-otp__digit {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: var(--ds-button-font-weight);
      font-size: var(--ds-input-field-font-size);
      line-height: var(--ds-button-s-line-height);
      color: var(--ds-input-value-color);
      pointer-events: none;
    }

    .ds-otp__help,
    .ds-otp__resend-label,
    .ds-otp__resend-waiting {
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-weight: 400;
      text-align: center;
      letter-spacing: 0;
    }

    .ds-otp__help {
      font-size: var(--ds-foundation-font-size-3xs);
      line-height: var(--ds-foundation-line-height-3xs);
      color: var(--ds-input-help-color-error);
    }

    .ds-otp__resend-label,
    .ds-otp__resend-waiting {
      font-size: var(--ds-foundation-font-size-2xs);
      line-height: var(--ds-foundation-line-height-2xs);
    }

    .ds-otp__resend {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: calc(var(--ds-input-gap) + var(--ds-input-prefix-gap));
      width: 100%;
      color: var(--ds-input-value-color);
    }

    .ds-otp__resend-waiting {
      color: var(--ds-input-help-color);
    }

    .ds-otp__resend-waiting strong {
      font-weight: 700;
    }

    .ds-otp--disabled {
      opacity: 0.44;
      pointer-events: none;
    }

    .ds-otp__continue {
      display: flex;
      justify-content: center;
      width: 100%;
    }
  `],
})
export class DsInputOtpComponent implements OnChanges, OnDestroy {
  private static _counter = 0;
  private resendTimerId: ReturnType<typeof setInterval> | null = null;
  private digitRevealTimers = new Map<number, ReturnType<typeof setTimeout>>();

  @ViewChildren('cellControl') private cellControls?: QueryList<ElementRef<HTMLInputElement>>;

  readonly inputId = `ds-input-otp-${++DsInputOtpComponent._counter}`;
  readonly helpId = `${this.inputId}-help`;

  @Input() value = '';
  @Input({ transform: numberAttribute }) length = 6;
  @Input() state: DsInputOtpState = 'default';
  @Input() sendMethod: DsInputOtpSendMethod = 'correo';
  @Input() labelCorreo = 'Ingresa la clave dinámica enviada a tu correo';
  @Input() labelTelefono = 'Ingresa la clave dinámica enviada a tu teléfono';
  @Input() bajadaCorreo = 'La clave fue enviada a fran********da@gmail.com, tienes tres intentos y 5 minutos para ingresarla';
  @Input() bajadaTelefono = 'La clave fue enviada al +569 ******69, tienes tres intentos y 5 minutos para ingresarla';
  @Input() helpText = 'Debes ingresar la clave';
  @Input({ transform: booleanAttribute }) showResendCode = true;
  @Input({ transform: booleanAttribute }) resendWaiting = false;
  @Input() resendPrompt = '¿No recibiste la clave?';
  @Input() resendWaitingText = 'Podrás reenviar una nueva dentro de';
  @Input({ transform: numberAttribute }) resendCountdownSeconds = 60;
  @Input() resendButtonLabel = 'Reenviar clave';
  @Input({ transform: booleanAttribute }) showContinueButton = true;
  @Input() continueLabel = 'Continuar';
  @Input({ transform: booleanAttribute }) continueDisabled = false;
  @Input({ transform: booleanAttribute }) disabled = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() completed = new EventEmitter<string>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();
  @Output() resendCode = new EventEmitter<MouseEvent>();
  @Output() continueClicked = new EventEmitter<MouseEvent>();

  focusedState = false;
  activeCellIndex = 0;
  revealedDigitIndexes = new Set<number>();
  resendRemainingSeconds = 0;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resendWaiting'] || changes['resendCountdownSeconds']) {
      if (this.resendWaiting) {
        this.startResendCountdown();
      } else {
        this.stopResendCountdown();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopResendCountdown();
    this.clearDigitRevealTimers();
  }

  get isDisabled(): boolean {
    return this.disabled || this.state === 'disabled';
  }

  get normalizedLength(): number {
    return this.length === 4 ? 4 : 6;
  }

  get normalizedValue(): string {
    return this.value.replace(/\D+/g, '').slice(0, this.normalizedLength);
  }

  get isComplete(): boolean {
    return this.normalizedValue.length === this.normalizedLength;
  }

  get isContinueDisabled(): boolean {
    return this.isDisabled || this.continueDisabled || !this.isComplete;
  }

  get isResendCounting(): boolean {
    return this.resendRemainingSeconds > 0;
  }

  get formattedResendTime(): string {
    const seconds = Math.max(0, this.resendRemainingSeconds);
    const minutesPart = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secondsPart = (seconds % 60).toString().padStart(2, '0');
    return `${minutesPart}:${secondsPart}`;
  }

  get cellIndexes(): number[] {
    return Array.from({ length: this.normalizedLength }, (_, index) => index);
  }

  get activeIndex(): number {
    return Math.min(this.activeCellIndex, this.normalizedLength - 1);
  }

  get expectedFocusIndex(): number {
    return Math.min(this.normalizedValue.length, this.normalizedLength - 1);
  }

  get isFocused(): boolean {
    return this.focusedState;
  }

  get resolvedLabel(): string {
    return this.sendMethod === 'telefono' ? this.labelTelefono : this.labelCorreo;
  }

  get resolvedMessage(): string {
    return this.sendMethod === 'telefono' ? this.bajadaTelefono : this.bajadaCorreo;
  }

  digitAt(index: number): string {
    return this.normalizedValue[index] ?? '';
  }

  isDigitRevealed(index: number): boolean {
    return this.revealedDigitIndexes.has(index);
  }

  focusCell(index: number): void {
    if (!this.isDisabled) {
      const nextIndex = this.getSelectableIndex(index);
      this.activeCellIndex = nextIndex;
      queueMicrotask(() => {
        this.cellControls?.get(nextIndex)?.nativeElement.focus();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  onCellFocus(index: number, event: FocusEvent): void {
    this.focusedState = true;
    const nextIndex = this.getSelectableIndex(index);
    this.activeCellIndex = nextIndex;
    if (index !== nextIndex) {
      this.focusCell(nextIndex);
    }
    this.focused.emit(event);
  }

  onCellBlur(event: FocusEvent): void {
    queueMicrotask(() => {
      const activeElement = document.activeElement;
      const stillInside = !!this.cellControls?.some((cell) => cell.nativeElement === activeElement);
      if (!stillInside) {
        this.focusedState = false;
        this.blurred.emit(event);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  onCellBeforeInput(index: number, event: Event): void {
    const inputEvent = event as InputEvent;
    if (inputEvent.inputType === 'deleteContentBackward' || inputEvent.inputType === 'deleteContentForward') {
      return;
    }

    const data = inputEvent.data ?? '';
    const digits = data.replace(/\D+/g, '');
    if (!digits) {
      if (data) {
        event.preventDefault();
      }
      return;
    }

    event.preventDefault();
    if (digits.length > 1) {
      this.commitValue(digits.slice(0, this.normalizedLength), Math.min(digits.length, this.normalizedLength - 1));
      return;
    }

    const targetIndex = this.expectedFocusIndex;
    this.commitValue(this.replaceAt(targetIndex, digits), Math.min(targetIndex + 1, this.normalizedLength - 1));
  }

  onCellInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D+/g, '');
    input.value = this.digitAt(index);
    if (!digits) {
      return;
    }

    if (digits.length > 1) {
      this.commitValue(digits.slice(0, this.normalizedLength), Math.min(digits.length, this.normalizedLength - 1));
      return;
    }

    const targetIndex = this.expectedFocusIndex;
    const nextValue = this.replaceAt(targetIndex, digits);
    this.commitValue(nextValue, Math.min(targetIndex + 1, this.normalizedLength - 1));
  }

  onCellKeydown(index: number, event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.key === 'Tab') {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusCell(this.expectedFocusIndex);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusCell(this.expectedFocusIndex);
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      const targetIndex = this.expectedFocusIndex;
      const nextIndex = this.normalizedValue[targetIndex] ? targetIndex : Math.max(0, targetIndex - 1);
      const nextValue = this.replaceAt(nextIndex, '');
      this.commitValue(nextValue, nextIndex);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      const targetIndex = this.expectedFocusIndex;
      this.commitValue(this.replaceAt(targetIndex, ''), targetIndex);
      return;
    }

    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault();
      const targetIndex = this.expectedFocusIndex;
      const nextValue = this.replaceAt(targetIndex, event.key);
      const nextIndex = Math.min(targetIndex + 1, this.normalizedLength - 1);
      this.commitValue(nextValue, nextIndex);
    }
  }

  onCellPaste(event: ClipboardEvent): void {
    const pastedValue = event.clipboardData?.getData('text') ?? '';
    const digits = pastedValue.replace(/\D+/g, '').slice(0, this.normalizedLength);
    if (!digits) {
      return;
    }

    event.preventDefault();
    this.commitValue(digits, Math.min(digits.length, this.normalizedLength - 1));
  }

  handleResend(event: MouseEvent): void {
    this.startResendCountdown();
    this.resendCode.emit(event);
  }

  private startResendCountdown(): void {
    this.stopResendCountdown();
    this.resendRemainingSeconds = Math.max(0, Math.floor(this.resendCountdownSeconds || 0));
    if (this.resendRemainingSeconds <= 0) {
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.resendTimerId = setInterval(() => {
      this.resendRemainingSeconds = Math.max(0, this.resendRemainingSeconds - 1);
      if (this.resendRemainingSeconds === 0) {
        this.stopResendCountdown();
      }
      this.changeDetectorRef.markForCheck();
    }, 1000);
    this.changeDetectorRef.markForCheck();
  }

  private stopResendCountdown(): void {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
    if (!this.resendWaiting) {
      this.resendRemainingSeconds = 0;
    }
  }

  private revealDigit(index: number): void {
    const safeIndex = this.clampIndex(index);
    const existingTimer = this.digitRevealTimers.get(safeIndex);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    this.revealedDigitIndexes.add(safeIndex);
    const timer = setTimeout(() => {
      this.revealedDigitIndexes.delete(safeIndex);
      this.digitRevealTimers.delete(safeIndex);
      this.changeDetectorRef.markForCheck();
    }, 700);
    this.digitRevealTimers.set(safeIndex, timer);
  }

  private clearDigitRevealTimers(): void {
    this.digitRevealTimers.forEach((timer) => clearTimeout(timer));
    this.digitRevealTimers.clear();
    this.revealedDigitIndexes.clear();
  }

  private replaceAt(index: number, digit: string): string {
    const chars = this.normalizedValue.split('');
    const safeIndex = Math.min(Math.max(index, 0), this.normalizedLength - 1);
    if (digit) {
      chars[safeIndex] = digit;
    } else {
      chars.splice(safeIndex, 1);
    }
    return chars.join('').slice(0, this.normalizedLength);
  }

  private commitValue(nextValue: string, nextIndex: number): void {
    const previousValue = this.normalizedValue;
    this.value = nextValue;
    this.activeCellIndex = Math.min(Math.max(nextIndex, 0), this.normalizedLength - 1);
    const revealedIndex = this.findRevealedIndex(previousValue, nextValue);
    if (revealedIndex !== null) {
      this.revealDigit(revealedIndex);
    }
    this.valueChange.emit(nextValue);
    if (nextValue.length === this.normalizedLength) {
      this.completed.emit(nextValue);
    }
    this.focusCell(this.activeCellIndex);
    this.changeDetectorRef.markForCheck();
  }

  private clampIndex(index: number): number {
    return Math.min(Math.max(index, 0), this.normalizedLength - 1);
  }

  private getSelectableIndex(index: number): number {
    return this.expectedFocusIndex;
  }

  private findRevealedIndex(previousValue: string, nextValue: string): number | null {
    const maxLength = Math.min(nextValue.length, this.normalizedLength);
    for (let index = 0; index < maxLength; index += 1) {
      if (nextValue[index] && previousValue[index] !== nextValue[index]) {
        return index;
      }
    }
    if (nextValue.length > previousValue.length) {
      return Math.min(nextValue.length - 1, this.normalizedLength - 1);
    }
    return null;
  }
}
